import { formatMoney } from '@theme/money-formatting';

const STORAGE_KEY = 'kpRecentlyViewedProductHandles';
const MAX_STORED_PRODUCTS = 12;
const HANDLE_PATTERN = /^[a-z0-9][a-z0-9-]*$/i;

class KpRecentlyViewed extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.#initialize(this.#controller.signal).catch(() => {
      this.hidden = true;
      this.removeAttribute('aria-busy');
    });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /**
   * Records the current handle, loads prior products, and reveals the section only when cards exist.
   * @param {AbortSignal} signal
   */
  async #initialize(signal) {
    this.hidden = true;

    const currentHandle = this.#normalizeHandle(this.dataset.currentHandle);
    const storedHandles = this.#readHandles();
    const limit = Math.min(Math.max(Number.parseInt(this.dataset.limit || '4', 10) || 4, 2), 8);
    const handlesToLoad = storedHandles
      .filter((handle) => handle !== currentHandle)
      .slice(0, Math.min(limit + 2, MAX_STORED_PRODUCTS));

    if (currentHandle) {
      this.#writeHandles([currentHandle, ...storedHandles]);
    }

    if (handlesToLoad.length === 0) return;

    this.setAttribute('aria-busy', 'true');

    try {
      const results = await Promise.allSettled(
        handlesToLoad.map((handle) => this.#fetchProduct(handle, signal))
      );

      if (signal.aborted) return;

      const products = results
        .filter((result) => result.status === 'fulfilled')
        .map((result) => result.value)
        .filter((product) => product && product.handle !== currentHandle)
        .slice(0, limit);

      if (products.length === 0) return;

      const list = this.querySelector('[data-kp-recent-products]');
      if (!(list instanceof HTMLUListElement)) return;

      const inner = this.querySelector('.kp-recently-viewed__inner');
      if (inner instanceof HTMLElement) {
        inner.style.setProperty('--kp-recent-columns', String(Math.min(products.length, 4)));
      }

      const fragment = document.createDocumentFragment();
      for (const product of products) {
        fragment.append(this.#createProductCard(product));
      }

      list.replaceChildren(fragment);
      this.hidden = false;
    } catch {
      this.hidden = true;
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  /** @returns {string[]} */
  #readHandles() {
    try {
      const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(value)) return [];

      return [...new Set(value.map((handle) => this.#normalizeHandle(handle)).filter(Boolean))].slice(
        0,
        MAX_STORED_PRODUCTS
      );
    } catch {
      return [];
    }
  }

  /** @param {string[]} handles */
  #writeHandles(handles) {
    try {
      const normalized = [...new Set(handles.map((handle) => this.#normalizeHandle(handle)).filter(Boolean))].slice(
        0,
        MAX_STORED_PRODUCTS
      );
      localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    } catch {
      // Storage can be unavailable in privacy modes; the section remains non-blocking.
    }
  }

  /**
   * @param {unknown} handle
   * @returns {string}
   */
  #normalizeHandle(handle) {
    if (typeof handle !== 'string') return '';
    const normalized = handle.trim();
    return HANDLE_PATTERN.test(normalized) ? normalized : '';
  }

  /**
   * @param {string} handle
   * @param {AbortSignal} signal
   * @returns {Promise<Record<string, any>>}
   */
  async #fetchProduct(handle, signal) {
    const basePath = this.dataset.productsUrl || '/products/';
    const endpoint = new URL(`${basePath}${encodeURIComponent(handle)}.js`, window.location.origin);

    if (endpoint.origin !== window.location.origin) throw new Error('Invalid product endpoint');

    const response = await fetch(endpoint, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      signal,
    });

    if (!response.ok) throw new Error(`Product request failed with ${response.status}`);

    const product = await response.json();
    if (!product || typeof product !== 'object' || !this.#normalizeHandle(product.handle)) {
      throw new Error('Invalid product response');
    }

    return product;
  }

  /**
   * Builds markup with DOM APIs so remote product data is never interpreted as HTML.
   * @param {Record<string, any>} product
   * @returns {HTMLLIElement}
   */
  #createProductCard(product) {
    const item = document.createElement('li');
    item.className = 'kp-recently-viewed__item';

    const article = document.createElement('article');
    article.className = 'kp-recently-viewed__card';

    const productUrl = this.#productUrl(product);
    const mediaLink = document.createElement('a');
    mediaLink.className = 'kp-recently-viewed__media-link';
    mediaLink.href = productUrl;
    mediaLink.setAttribute('aria-label', String(product.title || 'View product'));

    const media = document.createElement('span');
    media.className = 'kp-recently-viewed__media';

    const imageUrl = this.#imageUrl(product.featured_image || product.images?.[0]);
    if (imageUrl) {
      const image = document.createElement('img');
      image.className = 'kp-recently-viewed__image';
      image.src = imageUrl;
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      media.append(image);
    }

    mediaLink.append(media);

    const body = document.createElement('div');
    body.className = 'kp-recently-viewed__body';

    const title = document.createElement('a');
    title.className = 'kp-recently-viewed__title';
    title.href = productUrl;
    title.textContent = String(product.title || product.handle);

    const priceRow = document.createElement('div');
    priceRow.className = 'kp-recently-viewed__price-row';

    const price = document.createElement('span');
    price.className = 'kp-recently-viewed__price';
    price.textContent = this.#formatPrice(product.price_min ?? product.price);
    priceRow.append(price);

    const compareAtValue = Number(product.compare_at_price_max ?? product.compare_at_price ?? 0);
    const priceValue = Number(product.price_min ?? product.price ?? 0);
    if (Number.isFinite(compareAtValue) && compareAtValue > priceValue) {
      const compareAtPrice = document.createElement('s');
      compareAtPrice.className = 'kp-recently-viewed__compare-price';
      compareAtPrice.textContent = this.#formatPrice(compareAtValue);
      priceRow.append(compareAtPrice);
    }

    body.append(title, priceRow);
    article.append(mediaLink, body);
    item.append(article);

    return item;
  }

  /**
   * @param {Record<string, any>} product
   * @returns {string}
   */
  #productUrl(product) {
    const fallback = `${this.dataset.productsUrl || '/products/'}${encodeURIComponent(product.handle)}`;

    try {
      const url = new URL(fallback, window.location.origin);
      if (url.origin !== window.location.origin) return fallback;
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return fallback;
    }
  }

  /**
   * @param {unknown} value
   * @returns {string}
   */
  #imageUrl(value) {
    if (typeof value !== 'string' || value.length === 0) return '';

    try {
      const url = new URL(value, window.location.origin);
      return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : '';
    } catch {
      return '';
    }
  }

  /**
   * @param {unknown} value
   * @returns {string}
   */
  #formatPrice(value) {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '';

    const currency = this.dataset.currency || 'USD';
    const moneyFormat = this.dataset.moneyFormat || '{{amount}}';

    try {
      return formatMoney(amount, moneyFormat, currency);
    } catch {
      const locale = this.dataset.locale || document.documentElement.lang || 'en';
      const formatter = new Intl.NumberFormat(locale, { style: 'currency', currency });
      const precision = formatter.resolvedOptions().maximumFractionDigits;
      return formatter.format(amount / 10 ** precision);
    }
  }
}

if (!customElements.get('kp-recently-viewed')) {
  customElements.define('kp-recently-viewed', KpRecentlyViewed);
}
